





























import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator'
import ChatPanel from '@/views/Form/Conversation/ChatPanel.vue'
import FormPanel from '@/views/Form/Conversation/FormPanel.vue'
import { cloneDeep } from 'lodash'
import { Company } from '@/api/model/company'

export interface QuestionInputAttrs {
    [key: string]: string|object
}

export interface QuestionInputListeners {
    [key: string]: () => void
}

export interface Question {
    question: string
    model?: string
    input?: string
    inputAttrs?: QuestionInputAttrs,
    inputListeners?: QuestionInputListeners,
    choices?: string[]|object,
    onValidate?: (answer) => true|string|Promise<true|string>
    beforeEnter?: () => void|Promise<void>
    afterLeave?: () => void|Promise<void>
    formatAnswer?: (answer: string|number|Date, question: Question) => string
    defaultAnswer?: string|number|Date
}

@Component({
    components: {
        ChatPanel,
        FormPanel,
    },
})
export default class Conversation extends Vue {
    @Prop() questions!: Question[]
    typing: boolean = true
    @Prop() value!: any
    @Prop() company!: Company
    internalValue: any = null
    @Ref() form!: HTMLFormElement
    formVisible: boolean = false

    @Watch('value', { immediate: true })
    updateInternalValue(): void {
        this.internalValue = cloneDeep(this.value)
    }

    reset(): void {
        this.typing = true
        this.formVisible = false
        this.activeQuestionIndex = -1
        this.gotoNextQuestion()
    }

    activeQuestionIndex: number = -1
    get activeQuestion(): Question|null {
        return this.questions[this.activeQuestionIndex]
    }

    get nextQuestion(): Question|null {
        return this.questions[this.activeQuestionIndex + 1]
    }

    get previousQuestions(): Question[] {
        return this.questions
            .filter((question, index) => {
                return index < this.activeQuestionIndex
            })
            .reverse()
    }

    async gotoNextQuestion(): Promise<void> {
        // Hide form
        await this.hideForm()

        // Move to next question
        const afterLeave = this.activeQuestion ? this.activeQuestion.afterLeave : null
        this.activeQuestionIndex++
        const beforeEnter = this.activeQuestion ? this.activeQuestion.beforeEnter : null

        await Promise.all([
            this.startTyping(),
            afterLeave ? afterLeave() : null,
            beforeEnter ? beforeEnter() : null,
        ])

        await this.endTyping()

        // Show form
        await this.showForm()

        // Show next question if there is no form
        if (!this.hasForm && this.nextQuestion) {
            await this.gotoNextQuestion()
        }
    }

    async doTyping(duration: number = .5): Promise<void> {
        await this.startTyping(duration)
        await this.endTyping()
    }

    async startTyping(duration: number = .5): Promise<void> {
        this.typing = true
        await this.wait(duration * 1000)
    }

    async endTyping(): Promise<void> {
        this.typing = false
        await this.wait(500)
    }

    get hasForm(): boolean {
        if (!this.activeQuestion) {
            return false
        }

        if (!this.activeQuestion.model && !this.activeQuestion.input) {
            return false
        }

        return true
    }

    async showForm(): Promise<void> {
        if (!this.hasForm) {
            return
        }

        this.formVisible = true
        await this.wait(250)

        const input = this.form ? this.form.querySelector('input[type=text]') as HTMLInputElement : null
        if (input) {
            input.focus()
        }
    }

    async hideForm(): Promise<void> {
        this.formVisible = false
        await this.wait(250)
    }

    get componentStyle(): object {
        if (this.company) {
            return {
                '--widgetChatTextBackground': this.company.widgetChatTextBackground,
                '--widgetChatText': this.company.widgetChatText,
                '--widgetChatUserText': this.company.widgetChatUserText,
                '--widgetChatUserTextBorder': this.company.widgetChatUserTextBorder,
                '--widgetChatInputBorder': this.company.widgetChatInputBorder,
                '--widgetChatInputText': this.company.widgetChatInputText,
                '--widgetChatInputButtonActive': this.company.widgetChatInputButtonActive,
                '--widgetChatInputButtonActiveText': this.company.widgetChatInputButtonActiveText,
                '--widgetChatInputButtonInactive': this.company.widgetChatInputButtonInactive,
                '--widgetChatInputButtonInactiveText': this.company.widgetChatInputButtonInactiveText,
                '--widgetChatInputButton': this.company.widgetChatInputButton,
                '--widgetChatInputButtonText': this.company.widgetChatInputButtonText,
                '--widgetChatInputMultiText': this.company.widgetChatInputMultiText,
                '--widgetChatInputMultiBorder': this.company.widgetChatInputMultiBorder,
                '--widgetChatInputMultiSelectedText': this.company.widgetChatInputMultiSelectedText,
                '--widgetChatInputMultiSelectedBorder': this.company.widgetChatInputMultiSelectedBorder,
            }
        }

        return {}
    }

    onFormInput(answer): void {
        this.internalValue[this.activeQuestion.model] = answer
        this.$emit('input', this.internalValue)

        this.gotoNextQuestion()
    }

    mounted(): void {
        this.gotoNextQuestion()
    }

    async wait(milliseconds: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, milliseconds))
    }
}
